// src/DemoPage.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import {
  Container,
  Box,
  Typography,
  Button,
  Paper,
  CircularProgress,
  Chip,
  Grid,
  Card,
  CardContent,
  Fade,
  Zoom,
  TextField,
  InputAdornment,
  IconButton,
  Avatar,
} from '@mui/material';
import {
  Chat as ChatIcon,
  WhatsApp as WhatsAppIcon,
  Instagram as InstagramIcon,
  Telegram as TelegramIcon,
  Email as EmailIcon,
  Language as WebIcon,
  CheckCircle as CheckCircleIcon,
  AutoAwesome as AutoAwesomeIcon,
  Send as SendIcon,
  SmartToy as SmartToyIcon,
} from '@mui/icons-material';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

function DemoPage() {
  const { slug } = useParams();

  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [demoData, setDemoData] = useState(null);
  const [widgetLoaded, setWidgetLoaded] = useState(false);
  const [inlineChatInput, setInlineChatInput] = useState('');
  const [inlineChatStarted, setInlineChatStarted] = useState(false);

  // Embedded chat state
  const [embeddedMessages, setEmbeddedMessages] = useState([]);
  const [embeddedWs, setEmbeddedWs] = useState(null);
  const [isTyping, setIsTyping] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);
  const messagesContainerRef = useRef(null);
  const streamingContentRef = useRef('');

  // Scroll to bottom of messages (within container only, not page)
  const scrollToBottom = useCallback(() => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  }, []);

  // Send message to embedded chat
  const sendEmbeddedMessage = useCallback((content) => {
    content = (content || '').trim();
    if (!content || !embeddedWs || embeddedWs.readyState !== WebSocket.OPEN) return;

    // Add user message immediately
    setEmbeddedMessages(prev => [...prev, {
      role: 'user',
      content: content,
      created_at: new Date().toISOString(),
    }]);

    // Send via WebSocket
    embeddedWs.send(JSON.stringify({ type: 'message', content }));
    setInlineChatInput('');
  }, [embeddedWs]);

  // Connect to WebSocket for embedded chat
  useEffect(() => {
    if (!demoData?.widget_script) return;

    // Extract token from widget script
    const tokenMatch = demoData.widget_script.match(/data-token=["']([^"']+)["']/);
    if (!tokenMatch) {
      console.warn('[DemoPage] No widget token found for embedded chat');
      return;
    }

    const token = tokenMatch[1];

    // Extract the backend URL from the widget script src (same logic as widget itself)
    const srcMatch = demoData.widget_script.match(/src=["']([^"']+)["']/);
    let wsHost;
    if (srcMatch) {
      try {
        const scriptUrl = new URL(srcMatch[1]);
        wsHost = scriptUrl.host;
      } catch (e) {
        console.warn('[DemoPage] Failed to parse widget script URL, using current host');
        wsHost = window.location.host;
      }
    } else {
      wsHost = window.location.host;
    }

    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = `${wsProtocol}//${wsHost}/widget/ws/${token}`;

    console.log('[DemoPage] Connecting embedded chat WebSocket:', wsUrl);

    const ws = new WebSocket(wsUrl);

    ws.onopen = () => {
      console.log('[DemoPage] ✅ Embedded chat WebSocket connected');
      setWsConnected(true);

      // Initialize session - required before sending messages
      // Generate or reuse a visitor ID for session tracking
      let visitorId = sessionStorage.getItem('comturkai-embedded-visitor');
      if (!visitorId) {
        visitorId = 'embedded-' + Date.now() + '-' + Math.random().toString(36).substring(7);
        sessionStorage.setItem('comturkai-embedded-visitor', visitorId);
      }

      ws.send(JSON.stringify({
        type: 'init',
        visitor_id: visitorId,
        page_url: window.location.href,
        page_title: document.title,
        metadata: {
          userAgent: navigator.userAgent,
          language: navigator.language,
          referrer: document.referrer,
          screenWidth: window.screen.width,
          screenHeight: window.screen.height,
          source: 'embedded_chat',
        },
      }));
      console.log('[DemoPage] Sent session init for embedded chat');
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log('[DemoPage] Embedded chat received:', data.type);

        switch (data.type) {
          case 'connected':
            // Load existing messages only - styled greeting is shown in JSX when messages is empty
            if (data.messages && data.messages.length > 0) {
              setEmbeddedMessages(data.messages);
            }
            // Note: We intentionally don't use data.greeting here
            // The styled greeting in JSX (lines 695-716) is always shown when embeddedMessages is empty
            break;

          case 'stream':
            // Handle streaming tokens
            setIsTyping(false);
            streamingContentRef.current += data.content;
            setEmbeddedMessages(prev => {
              const lastMsg = prev[prev.length - 1];
              if (lastMsg && lastMsg.isStreaming) {
                // Update streaming message
                return [...prev.slice(0, -1), {
                  ...lastMsg,
                  content: streamingContentRef.current,
                }];
              } else {
                // Start new streaming message
                streamingContentRef.current = data.content;
                return [...prev, {
                  role: 'assistant',
                  content: data.content,
                  created_at: new Date().toISOString(),
                  isStreaming: true,
                }];
              }
            });
            break;

          case 'message':
            // Finalize message (end of stream or non-streaming)
            setEmbeddedMessages(prev => {
              const lastMsg = prev[prev.length - 1];
              if (lastMsg && lastMsg.isStreaming) {
                // Finalize streaming message
                streamingContentRef.current = '';
                return [...prev.slice(0, -1), {
                  ...lastMsg,
                  isStreaming: false,
                }];
              } else {
                // Add new message
                return [...prev, {
                  role: data.role,
                  content: data.content,
                  created_at: data.created_at || new Date().toISOString(),
                }];
              }
            });
            break;

          case 'typing':
            setIsTyping(data.is_typing);
            break;

          case 'error':
            console.error('[DemoPage] Embedded chat error:', data.message);
            break;
        }
      } catch (error) {
        console.error('[DemoPage] Failed to parse WebSocket message:', error);
      }
    };

    ws.onclose = () => {
      console.log('[DemoPage] Embedded chat WebSocket closed');
      setWsConnected(false);
    };

    ws.onerror = (error) => {
      console.error('[DemoPage] Embedded chat WebSocket error:', error);
    };

    setEmbeddedWs(ws);

    return () => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.close();
      }
    };
  }, [demoData]);

  // Scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [embeddedMessages, scrollToBottom]);

  // Load demo data
  useEffect(() => {
    const loadDemo = async () => {
      if (!slug) {
        setError('Invalid demo URL');
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(`${BACKEND_URL}/api/demos/public/${slug}`);
        setDemoData(response.data);

        // Track page view
        try {
          await axios.post(`${BACKEND_URL}/api/demos/public/${slug}/track`, {
            event_type: 'view'
          });
        } catch (trackErr) {
          console.warn('Failed to track view:', trackErr);
        }

      } catch (err) {
        console.error('Error loading demo:', err);
        if (err.response?.status === 404) {
          setError('Demo not found. Please check the URL.');
        } else {
          setError('Failed to load demo page. Please try again later.');
        }
      } finally {
        setLoading(false);
      }
    };

    loadDemo();
  }, [slug]);

  // Load Microsoft Clarity for session recording on demo pages
  useEffect(() => {
    const clarityProjectId = 'vb5nuuf2ww';

    // Only load if not already loaded
    if (!window.clarity) {
      (function (c, l, a, r, i, t, y) {
        c[a] = c[a] || function () { (c[a].q = c[a].q || []).push(arguments) };
        t = l.createElement(r); t.async = 1; t.src = "https://www.clarity.ms/tag/" + i;
        y = l.getElementsByTagName(r)[0]; y.parentNode.insertBefore(t, y);
      })(window, document, "clarity", "script", clarityProjectId);

      console.log('[DemoPage] Microsoft Clarity loaded for session recording');
    }
  }, []);

  // Inject widget script - DISABLED: Using embedded chat instead
  // The floating bubble widget is disabled to avoid having two chat interfaces.
  // The embedded chat in the hero section is now the primary CTA.
  useEffect(() => {
    // DISABLED: Floating widget is replaced by embedded chat
    // If you need to re-enable the floating widget, remove this early return
    console.log('[DemoPage] Floating widget DISABLED - using embedded chat instead');
    return;

    try {
      console.log('[DemoPage] Starting widget injection...');

      // Remove any existing widget script
      const existingScript = document.getElementById('comturk-widget-script');
      if (existingScript) {
        console.log('[DemoPage] Removing existing script');
        existingScript.remove();
      }

      // Remove existing widget container if present
      const existingWidget = document.querySelector('.comturk-widget-container');
      if (existingWidget) {
        console.log('[DemoPage] Removing existing widget container');
        existingWidget.remove();
      }

      // Parse the widget script to extract src and data-token
      const scriptMatch = demoData.widget_script.match(/src=["']([^"']+)["']/);
      const tokenMatch = demoData.widget_script.match(/data-token=["']([^"']+)["']/);

      if (scriptMatch && tokenMatch) {
        // New widget format: external script with data-token
        console.log('[DemoPage] Using new widget format');
        console.log('[DemoPage] Script src:', scriptMatch[1]);
        console.log('[DemoPage] Token:', tokenMatch[1]);

        const script = document.createElement('script');
        script.id = 'comturk-widget-script';
        script.src = scriptMatch[1];
        script.setAttribute('data-token', tokenMatch[1]);
        script.async = true;

        script.onload = () => {
          console.log('[DemoPage] ✅ Widget script loaded successfully');
          setWidgetLoaded(true);

          // Add pulsing animation to widget button for first 5 seconds
          setTimeout(() => {
            const widgetBtn = document.querySelector('.comturkai-bubble');
            if (widgetBtn) {
              // Add keyframes dynamically
              const style = document.createElement('style');
              style.textContent = `
                @keyframes widget-pulse {
                  0%, 100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(79, 70, 229, 0.7); }
                  50% { transform: scale(1.2); box-shadow: 0 0 25px 15px rgba(79, 70, 229, 0.5); }
                }
                .comturkai-bubble {
                  animation: widget-pulse 1s ease-in-out 5 !important;
                }
              `;
              document.head.appendChild(style);
            }
          }, 1000);

          // NOTE: Auto-expand removed - embedded chat is now the primary CTA
        };

        script.onerror = (err) => {
          console.error('[DemoPage] ❌ Widget script failed to load:', err);
        };

        document.body.appendChild(script);
        console.log('[DemoPage] ✅ Widget script injected');
      } else {
        // Legacy Chatwoot format: inline script content
        console.log('[DemoPage] Using legacy Chatwoot format');

        const scriptContent = demoData.widget_script
          .replace(/<script[^>]*>/gi, '')
          .replace(/<\/script>/gi, '')
          .trim();

        console.log('[DemoPage] Script content length:', scriptContent.length);

        const script = document.createElement('script');
        script.id = 'comturk-widget-script';
        script.type = 'text/javascript';
        script.text = scriptContent;
        document.body.appendChild(script);

        console.log('[DemoPage] ✅ Chatwoot widget script injected');
        setWidgetLoaded(true);
      }

    } catch (err) {
      console.error('[DemoPage] ❌ Error injecting widget script:', err);
    }

    // Cleanup on unmount
    return () => {
      const scriptToRemove = document.getElementById('comturk-widget-script');
      if (scriptToRemove) {
        scriptToRemove.remove();
      }

      // Remove widget container
      const widgetContainer = document.querySelector('.comturk-widget-container');
      if (widgetContainer) {
        widgetContainer.remove();
      }

      // Legacy Chatwoot cleanup
      const widgetBubble = document.querySelector('.woot-widget-bubble');
      const widgetHolder = document.querySelector('.woot-widget-holder');
      if (widgetBubble) widgetBubble.remove();
      if (widgetHolder) widgetHolder.remove();

      if (window.chatwootSDK) {
        delete window.chatwootSDK;
      }
      if (window.$chatwoot) {
        delete window.$chatwoot;
      }
    };
  }, [demoData]);

  // Loading state
  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        }}
      >
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Loading Demo...
          </Typography>
        </Paper>
      </Box>
    );
  }

  // Error state
  if (error || !demoData) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        }}
      >
        <Paper sx={{ p: 4, textAlign: 'center', maxWidth: 500 }}>
          <Typography variant="h5" color="error" gutterBottom>
            {error || 'Demo Not Available'}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Please contact us if you believe this is an error.
          </Typography>
        </Paper>
      </Box>
    );
  }

  const channels = [
    { name: 'Website Widget', icon: <WebIcon />, color: '#1976d2' },
    { name: 'WhatsApp', icon: <WhatsAppIcon />, color: '#25D366' },
    { name: 'Instagram', icon: <InstagramIcon />, color: '#E4405F' },
    { name: 'Telegram', icon: <TelegramIcon />, color: '#0088cc' },
    { name: 'Messenger', icon: <ChatIcon />, color: '#0084ff' },
    { name: 'Email', icon: <EmailIcon />, color: '#EA4335' },
  ];

  const features = [
    '24/7 instant responses',
    'Accurate answers from your knowledge base',
    'Multi-language support',
    'Seamless human handoff',
    'Analytics & insights',
    'Easy integration',
  ];

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        py: 4,
      }}
    >
      <Container maxWidth="lg">
        {/* IMPORTANT PRIVACY NOTICE - Simplified */}
        <Fade in timeout={600}>
          <Paper
            elevation={3}
            sx={{
              p: 2,
              mb: 4,
              backgroundColor: '#fff3cd',
              borderLeft: '4px solid #ff9800',
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: '#856404',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                textAlign: 'center',
              }}
            >
              ⚠️ IMPORTANT NOTICE - DEMO PURPOSES ONLY
            </Typography>
          </Paper>
        </Fade>

        {/* Header */}
        <Fade in timeout={800}>
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Zoom in timeout={1000}>
              <Box>
                <AutoAwesomeIcon
                  sx={{
                    fontSize: 80,
                    color: '#fff',
                    mb: 2,
                    filter: 'drop-shadow(0px 4px 12px rgba(0,0,0,0.3))'
                  }}
                />
              </Box>
            </Zoom>

            <Typography
              variant="h2"
              sx={{
                color: '#fff',
                fontWeight: 700,
                mb: 2,
                textShadow: '0px 2px 10px rgba(0,0,0,0.3)',
                fontSize: { xs: '2rem', md: '3.5rem' }
              }}
            >
              {demoData.page_title}
            </Typography>

            <Typography
              variant="h5"
              sx={{
                color: 'rgba(255,255,255,0.9)',
                mb: 1,
                textShadow: '0px 1px 5px rgba(0,0,0,0.2)',
                fontSize: { xs: '1.2rem', md: '1.5rem' }
              }}
            >
              Demo for: <strong>{demoData.team_name}</strong>
            </Typography>

            <Typography
              variant="body1"
              sx={{
                color: 'rgba(255,255,255,0.8)',
                fontSize: { xs: '0.9rem', md: '1rem' }
              }}
            >
              {demoData.page_description}
            </Typography>

            {/* Embedded Chat Interface - Main CTA */}
            <Paper
              elevation={6}
              sx={{
                mt: 4,
                p: { xs: 2, md: 4 },
                background: 'rgba(255,255,255,0.15)',
                backdropFilter: 'blur(20px)',
                borderRadius: 4,
                border: '1px solid rgba(255,255,255,0.3)',
                boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
              }}
            >
              <Grid container spacing={3} alignItems="center">
                {/* Left side - CTA Text */}
                <Grid item xs={12} md={5}>
                  <Typography
                    variant="h4"
                    sx={{
                      color: '#fff',
                      fontWeight: 700,
                      mb: 2,
                      fontSize: { xs: '1.5rem', md: '1.8rem' },
                      textShadow: '0 2px 4px rgba(0,0,0,0.2)',
                    }}
                  >
                    💬 Ask anything about <span style={{ color: '#FFD700' }}>{demoData.team_name}</span>
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: 'rgba(255,255,255,0.9)',
                      mb: 3,
                      fontSize: { xs: '0.95rem', md: '1.1rem' },
                    }}
                  >
                    Our AI answers instantly from their actual documents
                  </Typography>

                  {/* Example Questions as Clickable Chips */}
                  <Typography
                    variant="body2"
                    sx={{ color: 'rgba(255,255,255,0.8)', mb: 1, fontWeight: 500 }}
                  >
                    Try asking:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {[
                      { label: 'Services & Products', question: `What services does ${demoData.team_name} offer?` },
                      { label: 'Contact Info', question: 'How can I contact support?' },
                      { label: 'Pricing', question: 'What are the pricing options?' },
                    ].map((item, i) => (
                      <Chip
                        key={i}
                        label={item.label}
                        onClick={() => sendEmbeddedMessage(item.question)}
                        sx={{
                          bgcolor: 'rgba(255,255,255,0.25)',
                          color: '#fff',
                          fontWeight: 500,
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          '&:hover': {
                            bgcolor: 'rgba(255,255,255,0.4)',
                            transform: 'translateY(-2px)',
                          },
                        }}
                      />
                    ))}
                  </Box>
                </Grid>

                {/* Right side - Chat Preview Card */}
                <Grid item xs={12} md={7}>
                  <Paper
                    elevation={4}
                    sx={{
                      p: 0,
                      borderRadius: 3,
                      overflow: 'hidden',
                      background: '#fff',
                      boxShadow: '0 12px 40px rgba(0,0,0,0.15)',
                    }}
                  >
                    {/* Chat Header */}
                    <Box
                      sx={{
                        background: 'linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)',
                        p: 2,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                      }}
                    >
                      <Avatar
                        sx={{
                          bgcolor: 'rgba(255,255,255,0.2)',
                          width: 36,
                          height: 36,
                        }}
                      >
                        <SmartToyIcon sx={{ color: '#fff', fontSize: 20 }} />
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle2" sx={{ color: '#fff', fontWeight: 600 }}>
                          {demoData.team_name} AI Assistant
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                          Online • Answers in seconds
                        </Typography>
                      </Box>
                    </Box>

                    {/* Chat Messages Area */}
                    <Box ref={messagesContainerRef} sx={{ p: 2, minHeight: 200, maxHeight: 300, overflow: 'auto', bgcolor: '#f8f9fa' }}>
                      {/* Show greeting if no messages yet */}
                      {embeddedMessages.length === 0 && (
                        <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
                          <Avatar sx={{ bgcolor: '#4F46E5', width: 32, height: 32 }}>
                            <SmartToyIcon sx={{ fontSize: 18 }} />
                          </Avatar>
                          <Paper
                            sx={{
                              p: 1.5,
                              bgcolor: '#fff',
                              borderRadius: '4px 16px 16px 16px',
                              boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                              maxWidth: '80%',
                            }}
                          >
                            <Typography variant="body2" sx={{ color: '#333' }}>
                              👋 Hello! I'm the AI assistant for <strong>{demoData.team_name}</strong>.
                              I've been trained on their documents and can answer your questions instantly.
                              What would you like to know?
                            </Typography>
                          </Paper>
                        </Box>
                      )}

                      {/* Render actual messages */}
                      {embeddedMessages.map((msg, index) => (
                        <Box
                          key={index}
                          sx={{
                            display: 'flex',
                            gap: 1.5,
                            mb: 2,
                            justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
                          }}
                        >
                          {msg.role === 'assistant' && (
                            <Avatar sx={{ bgcolor: '#4F46E5', width: 32, height: 32 }}>
                              <SmartToyIcon sx={{ fontSize: 18 }} />
                            </Avatar>
                          )}
                          <Paper
                            sx={{
                              p: 1.5,
                              bgcolor: msg.role === 'user' ? '#4F46E5' : '#fff',
                              color: msg.role === 'user' ? '#fff' : '#333',
                              borderRadius: msg.role === 'user' ? '16px 16px 4px 16px' : '4px 16px 16px 16px',
                              boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                              maxWidth: '80%',
                            }}
                          >
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', textAlign: 'left' }}>
                              {msg.content}
                              {msg.isStreaming && (
                                <span style={{ opacity: 0.5, animation: 'blink 1s infinite' }}>█</span>
                              )}
                            </Typography>
                          </Paper>
                          {msg.role === 'user' && (
                            <Avatar sx={{ bgcolor: '#10B981', width: 32, height: 32, fontSize: 14 }}>
                              You
                            </Avatar>
                          )}
                        </Box>
                      ))}

                      {/* Typing indicator */}
                      {isTyping && (
                        <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
                          <Avatar sx={{ bgcolor: '#4F46E5', width: 32, height: 32 }}>
                            <SmartToyIcon sx={{ fontSize: 18 }} />
                          </Avatar>
                          <Paper sx={{ p: 1.5, bgcolor: '#fff', borderRadius: '4px 16px 16px 16px' }}>
                            <Typography variant="body2" sx={{ color: '#999' }}>
                              Typing...
                            </Typography>
                          </Paper>
                        </Box>
                      )}

                    </Box>

                    {/* Chat Input Area */}
                    <Box
                      sx={{
                        p: 2,
                        borderTop: '1px solid #eee',
                        bgcolor: '#fff',
                      }}
                    >
                      <TextField
                        fullWidth
                        placeholder="Type your question here..."
                        value={inlineChatInput}
                        onChange={(e) => setInlineChatInput(e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter' && inlineChatInput.trim()) {
                            sendEmbeddedMessage(inlineChatInput);
                          }
                        }}
                        variant="outlined"
                        size="small"
                        disabled={!wsConnected}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 3,
                            bgcolor: '#f8f9fa',
                            '&:hover': {
                              bgcolor: '#fff',
                            },
                          },
                        }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                color="primary"
                                onClick={() => sendEmbeddedMessage(inlineChatInput)}
                                disabled={!inlineChatInput.trim() || !wsConnected}
                                sx={{
                                  bgcolor: '#4F46E5',
                                  color: '#fff',
                                  '&:hover': { bgcolor: '#4338CA' },
                                  '&:disabled': { bgcolor: '#ccc', color: '#fff' },
                                }}
                              >
                                <SendIcon fontSize="small" />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
            </Paper>
          </Box>
        </Fade>

        {/* Main Content */}
        <Grid container spacing={4}>
          {/* Custom Content Section */}
          {demoData.custom_content && (
            <Grid item xs={12}>
              <Fade in timeout={1200}>
                <Paper
                  sx={{
                    p: 4,
                    background: 'rgba(255,255,255,0.95)',
                    backdropFilter: 'blur(10px)',
                    borderRadius: 3,
                    boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      fontSize: '1.1rem',
                      lineHeight: 1.8,
                      whiteSpace: 'pre-wrap',
                      color: '#333',
                    }}
                  >
                    {demoData.custom_content}
                  </Typography>
                </Paper>
              </Fade>
            </Grid>
          )}

          {/* AI Knowledge Base - Shows crawled URLs - LEFT SIDE */}
          {demoData.crawled_urls && demoData.crawled_urls.length > 0 && (
            <Grid item xs={12} md={6}>
              <Fade in timeout={1400}>
                <Paper
                  sx={{
                    p: 4,
                    background: 'rgba(255,255,255,0.95)',
                    backdropFilter: 'blur(10px)',
                    borderRadius: 3,
                    boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                    height: '100%',
                  }}
                >
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: '#333' }}>
                    📚 AI Knowledge Base
                  </Typography>

                  <Typography variant="body1" sx={{ color: '#666', mb: 3 }}>
                    This AI is trained on <strong>{demoData.crawled_urls.length} pages</strong> from{' '}
                    <strong>{demoData.team_name}</strong>'s website:
                  </Typography>

                  <Box sx={{
                    maxHeight: 250,
                    overflow: 'auto',
                    bgcolor: '#f9f9f9',
                    borderRadius: 2,
                    p: 2
                  }}>
                    {demoData.crawled_urls.map((url, index) => (
                      <Box
                        key={index}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          mb: 1,
                          p: 1,
                          bgcolor: '#fff',
                          borderRadius: 1,
                          border: '1px solid #eee'
                        }}
                      >
                        <WebIcon sx={{ color: '#4F46E5', mr: 1, fontSize: 18 }} />
                        <Typography
                          component="a"
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            color: '#4F46E5',
                            textDecoration: 'none',
                            fontSize: '0.85rem',
                            wordBreak: 'break-all',
                            '&:hover': { textDecoration: 'underline' }
                          }}
                        >
                          {url}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Paper>
              </Fade>
            </Grid>
          )}

          {/* RIGHT COLUMN: Features + Channels stacked */}
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4, height: '100%' }}>
              {/* Features Section */}
              <Fade in timeout={1500}>
                <Paper
                  sx={{
                    p: 4,
                    background: 'rgba(255,255,255,0.95)',
                    backdropFilter: 'blur(10px)',
                    borderRadius: 3,
                    boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                  }}
                >
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: '#333' }}>
                    🚀 What Our AI Can Do
                  </Typography>

                  <Box sx={{ mt: 2 }}>
                    {features.map((feature, index) => (
                      <Box
                        key={index}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          mb: 1.5,
                        }}
                      >
                        <CheckCircleIcon sx={{ color: '#4caf50', mr: 1.5, fontSize: 20 }} />
                        <Typography variant="body2" sx={{ color: '#555' }}>
                          {feature}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Paper>
              </Fade>

              {/* Supported Channels - Under Features */}
              <Fade in timeout={1600}>
                <Paper
                  sx={{
                    p: 4,
                    background: 'rgba(255,255,255,0.95)',
                    backdropFilter: 'blur(10px)',
                    borderRadius: 3,
                    boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                  }}
                >
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: '#333' }}>
                    📱 Supported Channels
                  </Typography>

                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Engage your customers wherever they are
                  </Typography>

                  <Grid container spacing={2}>
                    {channels.map((channel, index) => (
                      <Grid item xs={6} key={index}>
                        <Zoom in timeout={1800 + index * 100}>
                          <Card
                            sx={{
                              textAlign: 'center',
                              p: 1.5,
                              cursor: 'pointer',
                              transition: 'all 0.3s',
                              '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: '0 6px 20px rgba(0,0,0,0.15)',
                              },
                            }}
                          >
                            <Box sx={{ color: channel.color, mb: 0.5 }}>
                              {React.cloneElement(channel.icon, { sx: { fontSize: 32 } })}
                            </Box>
                            <Typography variant="caption" sx={{ fontWeight: 500 }}>
                              {channel.name}
                            </Typography>
                          </Card>
                        </Zoom>
                      </Grid>
                    ))}
                  </Grid>
                </Paper>
              </Fade>
            </Box>
          </Grid>

          {/* CTA Section */}
          <Grid item xs={12}>
            <Fade in timeout={2000}>
              <Paper
                sx={{
                  p: 5,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  borderRadius: 3,
                  boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
                  textAlign: 'center',
                }}
              >
                <Typography
                  variant="h4"
                  sx={{
                    color: '#fff',
                    fontWeight: 600,
                    mb: 2,
                  }}
                >
                  Ready to Transform Your Customer Support?
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    color: 'rgba(255,255,255,0.9)',
                    mb: 4,
                    maxWidth: 600,
                    mx: 'auto',
                  }}
                >
                  Experience the power of AI-driven customer support.
                  Try the demo above and see how we can help your business!
                </Typography>

                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                  <Button
                    variant="contained"
                    size="large"
                    sx={{
                      bgcolor: '#fff',
                      color: '#667eea',
                      px: 4,
                      py: 1.5,
                      fontSize: '1.1rem',
                      fontWeight: 600,
                      '&:hover': {
                        bgcolor: '#f5f5f5',
                        transform: 'scale(1.05)',
                      },
                      transition: 'all 0.3s',
                    }}
                    onClick={() => {
                      // Open chat widget
                      if (window.$chatwoot) {
                        window.$chatwoot.toggle();
                      }
                    }}
                  >
                    Try Live Demo Now
                  </Button>

                  <Button
                    variant="outlined"
                    size="large"
                    sx={{
                      borderColor: '#fff',
                      color: '#fff',
                      px: 4,
                      py: 1.5,
                      fontSize: '1.1rem',
                      fontWeight: 600,
                      '&:hover': {
                        borderColor: '#fff',
                        bgcolor: 'rgba(255,255,255,0.1)',
                      },
                    }}
                    href="mailto:info@comturk.com"
                  >
                    Contact Us
                  </Button>
                </Box>
              </Paper>
            </Fade>
          </Grid>

          {/* Widget Status Indicator */}
          {widgetLoaded && (
            <Grid item xs={12}>
              <Fade in timeout={2200}>
                <Box sx={{ textAlign: 'center' }}>
                  <Chip
                    icon={<CheckCircleIcon />}
                    label="Chat Widget Active - Click the icon in bottom right!"
                    color="success"
                    sx={{
                      fontSize: '1rem',
                      py: 2.5,
                      px: 2,
                      background: 'rgba(255,255,255,0.95)',
                      animation: 'pulse 2s infinite',
                      '@keyframes pulse': {
                        '0%, 100%': {
                          transform: 'scale(1)',
                        },
                        '50%': {
                          transform: 'scale(1.05)',
                        },
                      },
                    }}
                  />
                </Box>
              </Fade>
            </Grid>
          )}
        </Grid>

        {/* Footer */}
        <Box sx={{ mt: 6, textAlign: 'center' }}>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
            Powered by AI • Built with ❤️ by Comturk
          </Typography>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', mt: 1, display: 'block' }}>
            This is a demonstration page. All conversations are for demo purposes only.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}

export default DemoPage;


